import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import './App.css';
import HeadPoseDisplay from './components/HeadPoseDisplay';
import BluetoothControl from './components/BluetoothControl';
import USBControl from './components/USBControl';
import LEDControl from './components/LEDControl';
import ServoControl from './components/ServoControl';
import CameraView from './components/CameraView';
import LightConfig from './components/LightConfig';

function App() {
  const [headPose, setHeadPose] = useState({ pitch: 0, yaw: 0, roll: 0 });
  const [fps, setFps] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [usbPort, setUsbPort] = useState(null);
  const [autoTracking, setAutoTracking] = useState(false);
  const [cameraRunning, setCameraRunning] = useState(true);
  const [gazeData, setGazeData] = useState({ distance: 0, targetPoint: { x: 0, y: 0, z: 0 }, lightAngles: { pan: 90, tilt: 90 } });
  const socketRef = useRef(null);

  useEffect(() => {
    // Connect to Python backend via Socket.IO
    const socket = io('http://localhost:5000', {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
      forceNew: false
    });

    socketRef.current = socket;
    let cameraStarted = false;

    socket.on('connect', () => {
      console.log('Connected to backend, socket ID:', socket.id);
      setIsConnected(true);
      // Only start camera once
      if (!cameraStarted) {
        cameraStarted = true;
        // Wait a moment before starting camera
        setTimeout(() => {
          socket.emit('start_camera');
        }, 500);
      }
    });

    socket.on('connected', (data) => {
      console.log('Server message:', data.message);
    });

    socket.on('camera_started', (data) => {
      console.log('Camera started:', data.message);
      setCameraRunning(true);
    });

    socket.on('camera_stopped', (data) => {
      console.log('Camera stopped:', data.message);
      setCameraRunning(false);
      setAutoTracking(false); // Disable auto tracking when camera stops
    });

    socket.on('camera_status', (data) => {
      console.log('Camera status:', data.message);
    });

    socket.on('head_pose', (data) => {
      // console.log('Head pose data:', data); // Debug log
      setHeadPose({
        pitch: data.pitch,
        yaw: data.yaw,
        roll: data.roll
      });
      // Update gaze data if available
      if (data.distance !== undefined) {
        setGazeData({
          distance: data.distance,
          targetPoint: data.target_point || { x: 0, y: 0, z: 0 },
          lightAngles: data.light_angles || { pan: 90, tilt: 90 }
        });
      }
    });

    socket.on('fps', (data) => {
      setFps(data.fps);
    });

    socket.on('error', (data) => {
      console.error('Server error:', data.message);
      alert('Backend error: ' + data.message);
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from backend');
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      setIsConnected(false);
      cameraStarted = false;
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('Reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
      if (!cameraStarted) {
        cameraStarted = true;
        setTimeout(() => {
          socket.emit('start_camera');
        }, 500);
      }
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('Reconnection attempt', attemptNumber);
    });

    socket.on('reconnect_error', (error) => {
      console.error('Reconnection error:', error);
    });

    socket.on('reconnect_failed', () => {
      console.error('Reconnection failed');
      setIsConnected(false);
      cameraStarted = false;
    });

    return () => {
      console.log('Cleaning up socket connection');
      if (socket.connected) {
        socket.emit('stop_camera');
      }
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, []);

  // Auto-tracking logic moved to ServoControl.js to prevent duplicate commands
  // and better manage Bluetooth transmission rates.

  const sendCommand = async (command) => {
    // Check USB first
    if (usbPort && usbPort.writable) {
      if (usbPort.writable.locked) {
        console.warn('USB port is locked, skipping command:', command);
        return;
      }

      const writer = usbPort.writable.getWriter();

      try {
        const encoder = new TextEncoder();
        await writer.write(encoder.encode(command + '\n'));
        console.log('Sent USB command:', command);
        return;
      } catch (error) {
        console.error('USB Write Error:', error);
      } finally {
        writer.releaseLock();
      }
    }

    // Fallback to Bluetooth
    if (!bluetoothDevice || !bluetoothDevice.gatt.connected) {
      if (!usbPort) console.warn('Cannot send command: No connection');
      return;
    }

    try {
      // Arduino Nano BLE 33 service and characteristic UUIDs
      const serviceUUID = '19b10000-e8f2-537e-4f6c-d104768a1214';
      const characteristicUUID = '19b10001-e8f2-537e-4f6c-d104768a1214';

      // Get service (may need to reconnect if disconnected)
      let service;
      try {
        service = await bluetoothDevice.gatt.getPrimaryService(serviceUUID);
      } catch (serviceError) {
        console.error('Service error, reconnecting...', serviceError);
        // Try to reconnect
        await bluetoothDevice.gatt.connect();
        service = await bluetoothDevice.gatt.getPrimaryService(serviceUUID);
      }

      // Get characteristic
      const characteristic = await service.getCharacteristic(characteristicUUID);

      // Write command (try with response first, fallback to without response)
      const encoder = new TextEncoder();
      const commandBytes = encoder.encode(command + '\n');

      try {
        await characteristic.writeValueWithResponse(commandBytes);
      } catch (writeError) {
        // Fallback to write without response
        await characteristic.writeValueWithoutResponse(commandBytes);
      }

      console.log('Sent command:', command);
    } catch (error) {
      console.error('Error sending command:', error);
      // Try to reconnect if connection lost
      if (error.message.includes('not connected') || error.message.includes('GATT') || error.message.includes('disconnected')) {
        console.log('Connection lost, resetting device');
        setBluetoothDevice(null);
      }
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>🎯 Head Pose Control System</h1>
        <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
          {isConnected ? '🟢 Backend Connected' : '🔴 Backend Disconnected'}
        </div>
      </header>

      <div className="main-container">
        <div className="left-panel">
          <CameraView socket={socketRef.current} />
          <HeadPoseDisplay headPose={headPose} fps={fps} gazeData={gazeData} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <USBControl setUsbPort={setUsbPort} sendCommand={sendCommand} />
            <BluetoothControl
              bluetoothDevice={bluetoothDevice}
              setBluetoothDevice={setBluetoothDevice}
              sendCommand={sendCommand}
            />
          </div>
        </div>

        <div className="right-panel">
          <ServoControl
            bluetoothDevice={bluetoothDevice} // Passed for state checking, but sendCommand handles the actual transport
            usbPort={usbPort}
            sendCommand={sendCommand}
            autoTracking={autoTracking && cameraRunning}
            setAutoTracking={setAutoTracking}
            headPose={headPose}
            cameraRunning={cameraRunning}
            gazeData={gazeData}
          />
          <LightConfig socket={socketRef.current} />
          <LEDControl
            bluetoothDevice={bluetoothDevice}
            usbPort={usbPort}
            sendCommand={sendCommand}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
